import { Injectable, signal, inject } from '@angular/core';
import { catchError, filter, finalize, map, retry, switchMap, tap } from 'rxjs/operators';
import { Observable, ReplaySubject, throwError, timer } from 'rxjs';
import {
  CustomPaymentType,
  LAST_USED_CRYPTO_CURRENCY,
  LAST_USED_FIAT_CURRENCY,
  Payment,
  PaymentsMethod,
} from '../../vendor/payment-v2/ss-payment-types';
import { SsPaymentsV2Service } from '../../vendor/payment-v2/ss-payment.service';
import { UserService } from './user.service';
import { CookieService } from 'ngx-unificator/services';
import { EnvironmentService } from '../environment.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { PlatformService } from '../platform.service';
import { ModalService } from '../../modal/modal.service';
import { WindowService } from '../window.service';
import { LibError } from 'payments-lib-types';
import { ToastMessageService } from '../../shared/modules/toast-message/toast-message.service';
import { ArrayService } from '../array.service';
import { FormsErrorHandlerService } from '../forms-error-handler.service';
import { isObjectEmpty } from 'ngx-unificator/helpers';
import { CurrencyService } from '../currency.service';

export const GOOGLEPAY: string = 'impaya~google-pay';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserPaymentV2Service {
  private _ssPaymentsV2 = inject(SsPaymentsV2Service);
  private _cookie = inject(CookieService);
  private _env = inject(EnvironmentService);
  private _platform = inject(PlatformService);
  private _modals = inject(ModalService);
  private _window = inject(WindowService);
  private _toastMessage = inject(ToastMessageService);
  private _array = inject(ArrayService);
  private _formErrors = inject(FormsErrorHandlerService);
  private _currency = inject(CurrencyService);


  public isDepositAction = signal<boolean>(false);

  /**3
   * Current payment action
   */
  public currentPaymentAction: Payment.Action;

  /**
   * Selected payment method fiat
   */
  public selectedFiatPaymentMethod: PaymentsMethod;

  /**
   * Selected payment method crypto
   */
  public selectedCryptoPaymentMethod: PaymentsMethod;

  /**
   * Selected multi protocol string
   */
  public selectedProtocol: string;

  /**
   * No fiat wallets
   */
  public isNoFiatWallet: boolean;

  public isEmptyModalShow: boolean;

  /**
   * Mocked payment list
   */
  public mockedPaymentList = ['accentpay-eco_payz', 'devcode-creditcard', 'finteqhub-neosurf'];

  /**
   * Payment banner
   */
  public banner = signal<{ htmlContent: string, htmlContentText: string, htmlContentImg: string }>(null);

  /**
   * View payment list for mobile version
   */
  public isPaymentListView: boolean;

  /**
   * Second deposit step if AB test exist
   */
  public isSecondDepositStep = signal(false);

  /**
   * Payment fiat methods list source
   */
  public paymentFiatMethodsList: ReplaySubject<PaymentsMethod[]> = new ReplaySubject(1);

  /**
   * Payment crypto methods list source
   */
  public paymentCryptoMethodsList: ReplaySubject<PaymentsMethod[]> = new ReplaySubject(1);

  /**
   * Protected access to payment method list source
   */
  public paymentFiatMethodsList$: Observable<PaymentsMethod[]> = this.paymentFiatMethodsList.asObservable();

  /**
   * Protected access to payment method list source
   */
  public paymentCryptoMethodsList$: Observable<PaymentsMethod[]> = this.paymentCryptoMethodsList.asObservable();

  public exchangeRateGiveAmount: number;

  /**
   * User service to avoid circular dependency
   * @private
   */
  private _user: UserService;

  /**
   * For avoiding circular dependency
   *
   * @param service
   */
  set userService(service: UserService) {
    this._user = service;
  }

  /**
   * Is deposit state
   */
  public get isDeposit(): boolean {
    return this.currentPaymentAction === Payment.Action.Deposit;
  }

  public handleDepositClick() {
    this.isDepositAction.set(true);
  }

  public getPaymentsList$(paymentType: CustomPaymentType) {
    return this._user.currency$.pipe(
      filter(currency => Boolean(currency?.length)),
      switchMap((currency) => {
        if (this._currency.isCryptoCurrency(currency) && paymentType === CustomPaymentType.FIAT) {
          return this._user.changeCurrencyAcc(this.getLastUsedCurrency(paymentType)).pipe(
            switchMap(() => this._updatePaymentMethodsList(currency, paymentType)),
          );
        } else {
          return this._updatePaymentMethodsList(currency, paymentType);
        }
      }),
      tap((list) => {

        const methodWithSavedAccount = list.find(e =>
          e.savedProfiles?.length > 0);
        const firstAvailableMethod = list[0];

        if (paymentType === CustomPaymentType.FIAT) {
          this.paymentFiatMethodsList.next(list);
          this.selectFiatPayment(methodWithSavedAccount || firstAvailableMethod);
        } else {
          this.paymentCryptoMethodsList.next(list);
          this.selectCryptoPayment(methodWithSavedAccount || firstAvailableMethod);
        }

      }),
      tap(list => {
        if (list.length === 0 && !this.isNoFiatWallet) {
          this.isEmptyModalShow = true;
          this.openModalEmptyPayments(
            this.currentPaymentAction,
            paymentType,
          );
        } else {
          this.isEmptyModalShow = false;
        }
      }),
    );
  }

  /**
   * Select fiat payment method
   * @param paymentMethod
   */
  public selectFiatPayment(paymentMethod: PaymentsMethod) {
    this.selectedFiatPaymentMethod = paymentMethod;
    this.isSecondDepositStep.set(false);
    if (this.selectedFiatPaymentMethod?.currency) {
      this._saveLastUsedFiatCurrency(this.selectedFiatPaymentMethod?.currency);
    }
  }

  /**
   * Select crypto payment method
   * @param paymentMethod
   */
  public selectCryptoPayment(paymentMethod: PaymentsMethod) {
    this.selectedCryptoPaymentMethod = paymentMethod;
    this.isSecondDepositStep.set(false);
    if (this.selectedCryptoPaymentMethod?.originalCryptoCurrency) {
      this._saveLastUsedCryptoCurrency(this.selectedCryptoPaymentMethod?.originalCryptoCurrency);
    }
  }

  /**
   * Submit Deposit / Cashout
   */
  public submitPayment(paymentSubmitData: any, paymentType: CustomPaymentType) {

    const submitData = paymentType === CustomPaymentType.FIAT ? {
      id: this.selectedFiatPaymentMethod.id,
      currency: this.selectedFiatPaymentMethod.currency,
      action: this.currentPaymentAction,
      data: paymentSubmitData?.formData,
      savedProfileId: paymentSubmitData?.savedProfileId?.id,
    } : {
      id: this.selectedCryptoPaymentMethod.id,
      currency: this.selectedCryptoPaymentMethod.currency,
      action: this.currentPaymentAction,
      data: paymentSubmitData?.formData,
      savedProfileId: paymentSubmitData?.savedProfileId?.id,
    };

    return this._ssPaymentsV2.submitForm(
      submitData?.id,
      submitData?.currency,
      submitData?.action,
      paymentType,
      submitData?.data,
      submitData?.savedProfileId,
      paymentSubmitData?.playerFieldsData,
    ).pipe(
      retry({
        count: 1,
        delay: (error: LibError.SubmitError) => {
          if (error?.statusCode?.includes(LibError.Status.NoProviderFound) &&
            !isObjectEmpty(paymentSubmitData?.playerFieldsData)) {
            return timer(5000);
          }
          return throwError(() => error);
        },
      }),
      catchError((error: LibError.SubmitError) => {
        console.error(error);
        this._resolvePaymentFieldErrors(paymentSubmitData, error);
        return throwError(() => error);
      }),
      tap(e => this._resolvePaymentMessage(e)),
    );

  }

  /**
   * Show payments list for mobile device
   */
  public showPaymentsList() {
    this.isPaymentListView = !this.isPaymentListView;
  }

  /**
   * Open change currency modal
   * @param selectedCurrency
   * @param action
   * @param paymentType
   */
  public async openModalEmptyPayments(
    action: Payment.Action,
    paymentType: CustomPaymentType,
  ) {
    const component = await import('../../modal/components/lazy/empty-payments-modal/empty-payments-modal.component');
    const modal = await this._modals.openLazy(
      component?.EmptyPaymentsModalComponent,
      {
        template: 'SMALL',
        data: {
          action,
          paymentType,
        },
        disableOverlayClosing: true,
      },
    );
    modal.onResult().pipe().subscribe(close => {
      if (close === 'onClickClose') {
        this._user.changeCurrencyAcc(this.getLastUsedCurrency(paymentType)).pipe(
          switchMap(() => this._updatePaymentMethodsList(this.getLastUsedCurrency(paymentType), paymentType)),
        ).subscribe();
      }
    });
  }

  /**
   * Save last used fiat currency
   * @param currency
   */
  private _saveLastUsedFiatCurrency(currency: string) {
    if (this._platform.isBrowser) {
      this._cookie.set(LAST_USED_FIAT_CURRENCY, currency ? currency :
        this._env.dataEnv()?.currency?.short, 999, '/', (window as any).location.hostname);
    }
  }

  /**
   * Save last used cryptocurrency
   * @param currency
   */
  private _saveLastUsedCryptoCurrency(currency: string) {
    if (this._platform.isBrowser) {
      this._cookie.set(LAST_USED_CRYPTO_CURRENCY, currency, 999, '/', (window as any).location.hostname);
    }
  }

  /**
   * Get last used fiat currency
   */
  public getLastUsedCurrency(paymentType: CustomPaymentType) {
    if (paymentType === CustomPaymentType.FIAT) {
      return this._cookie.get(LAST_USED_FIAT_CURRENCY) ? this._cookie.get(LAST_USED_FIAT_CURRENCY) : this._user.currentCurrency.currency;
    } else {
      return this._cookie.get(LAST_USED_CRYPTO_CURRENCY) ? this._cookie.get(LAST_USED_CRYPTO_CURRENCY) : this._user.currentCurrency.currency;
    }
  }

  /**
   * Update current payment methods list
   * @param currency
   * @param paymentType
   * @private
   */
  private _updatePaymentMethodsList(currency: string, paymentType: CustomPaymentType) {
    this.selectedFiatPaymentMethod = null;
    return this._ssPaymentsV2.fetchMethods(currency, this.currentPaymentAction).pipe(
      map((list: any[]) => list.map(method => ({
        ...method,
        currency,
        img: method.iconSrc,
        custom_img: paymentType !== CustomPaymentType.CRYPTO ? `/assets/svg/payments/fiat/${method.provider}-${method.brand}.svg` :
          `assets/svg/payments/coinspaid/color/coinspaid_coinspaid-86_${this._cutCryptoCurrencyFromId(method.id)}_coinspaid.svg`,
        originalCryptoCurrency: paymentType === CustomPaymentType.CRYPTO ? this._cutCryptoCurrencyFromId(method.id) : null,
      }))),
      map((list: PaymentsMethod[]) => list?.filter(method => method.brand !== GOOGLEPAY ||
        (method.brand !== GOOGLEPAY && (this._window.isIphoneDevices || this._window.isIpadOsDevices)))),
      map((list: PaymentsMethod[]) => list?.filter(method => method.id !== Payment.ComplementaryPaymentMethod.MercuryoFiat)),
      map((list: PaymentsMethod[]) =>
        list?.filter((method) => paymentType === CustomPaymentType.FIAT ?
          method.type !== Payment.MethodType.Crypto : method.type === Payment.MethodType.Crypto)),
    );
  }

  private _cutCryptoCurrencyFromId(str: string) {
    const match = str.match(/coinspaid_(.*?)_coinspaid/);
    if (match) {
      const result = match[1];
      return result.split('_').pop();
    } else {
      return null;
    }
  };

  /**
   * The function `_resolvePaymentMessage` displays a toast message if the payment status is 'notify' and
   * the action is 'Cashout'.
   * @param e - The parameter "e" is an object that represents a payment event. It may have a "status"
   * property that indicates the status of the payment.
   */
  private _resolvePaymentMessage(e) {
    if (e?.status === 'notify') {
      if (this.currentPaymentAction === Payment.Action.Cashout) {
        this._toastMessage.info('t.cashout-request-received');
      }
    }
    if (e?.status === 'iframe_opened') {
      if (this.isSecondDepositStep()) {
        this.isSecondDepositStep.set(false);
      }
    }
  }

  /**
   * The function `_resolvePaymentFieldErrors` handles and displays errors related to payment fields and
   * updates user information if necessary.
   * @param paymentData - `paymentData` seems to be an object containing data related to a payment
   * process. It likely includes information about the payment form, player fields data, and possibly
   * other payment-related details.
   * @param error - The `_resolvePaymentFieldErrors` function takes two parameters: `paymentData` and
   * `error`. The `error` parameter is of type `LibError.SubmitError`. This function is responsible for
   * handling errors related to payment fields. It checks the type of error and then processes any common
   * errors, method
   */
  private _resolvePaymentFieldErrors(paymentData, error: LibError.SubmitError) {
    if (error.type === LibError.Type.Params || error.type === LibError.Type.Request) {
      if (error.commonErrors?.length || error?.methodFieldsErrors || error?.playerFieldsErrors || error.amountFieldErrors) {
        this._array.fromObjectValues(error.commonErrors || error.methodFieldsErrors || error?.playerFieldsErrors || error.amountFieldErrors)
          .forEach(currentError => this._toastMessage.error(currentError));
        const fieldErrors = {
          ...(error?.methodFieldsErrors || {}),
          ...(error?.playerFieldsErrors || {}),
          ...(error?.amountFieldErrors || {}),
        };
        this._formErrors.applyFormErrors(paymentData.form, { errors: { ...fieldErrors } }, true);
      }
    }
    if (Object.keys(paymentData?.playerFieldsData)?.length && !error?.playerFieldsErrors) {
      this._user.applyUserInfo({ ...this._user.info, ...paymentData?.playerFieldsData });
    }
  }
}
